import {
  Box,
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useCalendarContext } from 'pages/processor/calendar/CalendarProvider';

import { JobTagChip } from './job-tag-chip';
import { JobTagIcon } from './job-tag-icon';
import { useJobTags } from './use-job-tags';

/**
 * The calendar's tag filter: a multi-select of the enabled tags, filtering
 * jobs to those carrying ANY selected tag.
 *
 * Deliberately NOT folded into `EventTypeFilterSelect`. That control already
 * splits one selection across two different context stores by string prefix;
 * a third group would make it worse.
 *
 * NOTHING selected means NO filter — not "show untagged jobs". Selecting
 * every tag is not the same thing and is left to mean exactly what it says,
 * so unlike the species picker an empty selection stays empty (it reads as
 * "All jobs") rather than normalizing to everything.
 * @returns the filter block, or null when the vendor has no tags.
 */
export function JobTagFilterSelect() {
  const { tagFilters, setTagFilters, toggleTagFilter } = useCalendarContext();
  const { enabledTags, jobTagsEnabled } = useJobTags();

  if (!jobTagsEnabled || enabledTags.length === 0) return null;

  const tagFor = (id: string) => enabledTags.find((tag) => tag._id === id);

  const handleChange = (next: string[]) => {
    // Toggle the one option the user clicked rather than rewriting the list,
    // so the context's own URL sync sees a single change.
    const nextSet = new Set(next);
    const selectedSet = new Set(tagFilters);
    const changed =
      next.find((value) => !selectedSet.has(value)) ??
      tagFilters.find((value) => !nextSet.has(value));
    if (changed) toggleTagFilter(changed);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.secondary">
          Tags
        </Typography>
        {tagFilters.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => setTagFilters([])}
            sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
          >
            Reset
          </Button>
        )}
      </Stack>
      <Box sx={{ mt: 0.5 }}>
        {/* No visible label: the heading above already names the control. */}
        <FormControl size="small" fullWidth>
          <Select
            multiple
            displayEmpty
            inputProps={{ 'aria-label': 'Tags' }}
            value={tagFilters}
            onChange={(e) =>
              handleChange(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value,
              )
            }
            renderValue={(values) =>
              values.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All jobs
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {values.map((value) => {
                    const tag = tagFor(value);
                    return tag ? (
                      <JobTagChip
                        key={value}
                        tag={tag}
                        color="primary"
                        variant="filled"
                      />
                    ) : null;
                  })}
                </Box>
              )
            }
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          >
            {enabledTags.map((tag) => (
              <MenuItem key={tag._id} value={tag._id} dense>
                <Checkbox
                  size="small"
                  checked={tagFilters.includes(tag._id ?? '')}
                  sx={{ py: 0.25 }}
                />
                <JobTagIcon
                  iconKey={tag.icon}
                  sx={{ fontSize: 15, mr: 1, flexShrink: 0 }}
                />
                <ListItemText
                  primary={tag.name}
                  slotProps={{ primary: { variant: 'body2' } }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
